import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Check, 
  X, 
  Package,
  RefreshCw,
  Gift,
  AlertTriangle,
  AlertCircle,
  Camera,
  Calendar,
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  Copy,
  Clock,
  CreditCard,
  ExternalLink,
  CheckCircle,
  Wallet,
  Loader2,
  Trash2,
  Upload,
  Store,
  Send,
  RotateCcw,
  FileDown,
  MailPlus
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { getReturnRequest, getRMA, updateReturnStatus, updateRMAStatus, checkPaymentStatus, deleteReturnRequest, deleteRMA, manualStatusUpdateReturn, manualStatusUpdateRMA, approveRejectExchange, returnWorkflowAction, rmaWorkflowAction, uploadWaybill, sendWaybillEmail, downloadWaybill, getAppSettings, markAsPaid, notifyStorePickup, checkFulfillmentStatus, getEmailLog, resendEmail, downloadReceipt, getPendingEmail, sendPendingEmail } from '@/lib/api';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import api from '@/lib/api';

// Button to send RECAP email to override address
function SendToOverrideButton({ requestId, overrideEmail, onDone }) {
  const [isSending, setIsSending] = useState(false);

  const handleSend = async () => {
    setIsSending(true);
    try {
      await api.post(`/admin/returns/${requestId}/send-all-to-override`);
      toast.success(`Email RECAP inviata a ${overrideEmail}`);
      if (onDone) onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore nell\'invio');
    }
    setIsSending(false);
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleSend}
      disabled={isSending}
      className="mt-2 w-full h-8 text-xs text-blue-700 border-blue-200 hover:bg-blue-50"
      data-testid="send-to-override-btn"
    >
      {isSending ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Send className="w-3 h-3 mr-1.5" />}
      Invia email RECAP a {overrideEmail}
    </Button>
  );
}

// Small component for override email
function OverrideEmailField({ requestId, currentEmail, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [email, setEmail] = useState(currentEmail || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    const trimmed = email.trim();
    if (trimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error('Email non valida');
      return;
    }
    setIsSaving(true);
    try {
      await api.put(`/admin/returns/${requestId}/override-email`, { email: trimmed || null });
      toast.success(trimmed ? 'Email alternativa salvata' : 'Email alternativa rimossa');
      setIsEditing(false);
      onUpdate();
    } catch (e) {
      toast.error('Errore nel salvataggio');
    }
    setIsSaving(false);
  };

  return (
    <div className="mt-4 pt-4 border-t border-gray-100" data-testid="override-email-section">
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-xs text-gray-400">Email alternativa per comunicazioni</p>
        {!isEditing && (
          <button onClick={() => { setEmail(currentEmail || ''); setIsEditing(true); }} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
            {currentEmail ? 'Modifica' : 'Aggiungi'}
          </button>
        )}
      </div>
      {isEditing ? (
        <div className="flex gap-2">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="nuova@email.com"
            className="text-sm h-8 flex-1"
            data-testid="override-email-input"
          />
          <Button size="sm" onClick={handleSave} disabled={isSaving} className="h-8 text-xs bg-gray-900 hover:bg-gray-800" data-testid="save-override-email-btn">
            {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Salva'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)} className="h-8 text-xs">
            Annulla
          </Button>
        </div>
      ) : currentEmail ? (
        <div>
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-blue-700">{currentEmail}</p>
            <button
              onClick={() => { setEmail(currentEmail); setIsEditing(true); }}
              className="text-xs text-gray-400 hover:text-blue-600"
            >
              Modifica
            </button>
          </div>
          <SendToOverrideButton requestId={requestId} overrideEmail={currentEmail} onDone={onUpdate} />
        </div>
      ) : (
        <p className="text-xs text-gray-300 italic">Non impostata</p>
      )}
    </div>
  );
}

const STATUS_CONFIG = {
  // New workflow states
  RICHIESTA: { label: 'Richiesta', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  RICHIESTA_DA_VERIFICARE: { label: 'Da Verificare', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  IN_ATTESA_DI_ARRIVO: { label: 'In Attesa di Arrivo', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  CAMBIO_IN_LAVORAZIONE: { label: 'Cambio in Lavorazione', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  COMPLETATO: { label: 'Completato', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  RESO_RIFIUTATO: { label: 'Reso Rifiutato', color: 'bg-red-100 text-red-700 border-red-200' },
  ARCHIVIATO: { label: 'Archiviato', color: 'bg-gray-100 text-gray-500 border-gray-200' },
  // Legacy states
  pending: { label: 'In attesa', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  requested: { label: 'Richiesto', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  approved: { label: 'Approvato', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  processing: { label: 'In elaborazione', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  shipped: { label: 'Spedito', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  completed: { label: 'Completato', color: 'bg-gray-100 text-gray-600 border-gray-200' },
  rejected: { label: 'Rifiutato', color: 'bg-red-100 text-red-700 border-red-200' },
  converted_no_return: { label: 'Convertito', color: 'bg-teal-100 text-teal-700 border-teal-200' }
};

// All available states for manual update
const MANUAL_STATUS_OPTIONS = [
  { value: 'RICHIESTA', label: 'Richiesta' },
  { value: 'RICHIESTA_DA_VERIFICARE', label: 'Da Verificare' },
  { value: 'IN_ATTESA_DI_ARRIVO', label: 'In Attesa di Arrivo' },
  { value: 'CAMBIO_IN_LAVORAZIONE', label: 'Cambio in Lavorazione' },
  { value: 'COMPLETATO', label: 'Completato' },
  { value: 'RESO_RIFIUTATO', label: 'Reso Rifiutato' },
  { value: 'ARCHIVIATO', label: 'Archiviato' },
];

const REASON_LABELS = {
  'too_small': 'Troppo piccolo',
  'too_large': 'Troppo grande',
  'different_fit': 'Vestibilità diversa',
  'different_color': 'Colore diverso',
  'defect': 'Difetto prodotto',
  'changed_mind': 'Ho cambiato idea',
  'other': 'Altro motivo'
};

const AdminRequestDetail = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [isRMA, setIsRMA] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [adminNotes, setAdminNotes] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [copied, setCopied] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  // Manual status update
  const [showManualStatus, setShowManualStatus] = useState(false);
  const [manualNewStatus, setManualNewStatus] = useState('');
  const [suppressEmail, setSuppressEmail] = useState(true);
  const [rejectionReason, setRejectionReason] = useState('');
  const [productionDateNote, setProductionDateNote] = useState('');
  const [approvalAction, setApprovalAction] = useState(''); // 'approve' or 'reject'
  // Waybill
  const [waybillEnabled, setWaybillEnabled] = useState(false);
  const [isUploadingWaybill, setIsUploadingWaybill] = useState(false);
  const [isSendingWaybill, setIsSendingWaybill] = useState(false);
  const [showWaybillConfirm, setShowWaybillConfirm] = useState(false);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);
  const [workflowConfig, setWorkflowConfig] = useState({});
  // Store delivery notification
  const [isNotifyingStore, setIsNotifyingStore] = useState(false);
  const [appSettings, setAppSettings] = useState({});
  // Email log
  const [emailLog, setEmailLog] = useState([]);
  const [isLoadingEmailLog, setIsLoadingEmailLog] = useState(false);
  const [resendingIndex, setResendingIndex] = useState(null);
  const [isDownloadingReceipt, setIsDownloadingReceipt] = useState(false);

  // Pending email
  const [pendingEmail, setPendingEmail] = useState(null);
  const [isSendingPending, setIsSendingPending] = useState(false);
  const [showPendingConfirm, setShowPendingConfirm] = useState(false);

  const loadPaymentStatus = useCallback(async () => {
    if (!request || isRMA || !request.draft_order?.id) return;
    
    setIsCheckingPayment(true);
    try {
      const status = await checkPaymentStatus(requestId);
      setPaymentStatus(status);
      
      // Refresh request data if payment was just completed
      if (status.just_updated) {
        const data = await getReturnRequest(requestId);
        setRequest(data);
        toast.success('Pagamento confermato!');
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    }
    setIsCheckingPayment(false);
  }, [request, isRMA, requestId]);

  const handleMarkAsPaid = async () => {
    setIsMarkingPaid(true);
    try {
      const result = await markAsPaid(requestId);
      setRequest(prev => ({
        ...prev,
        payment_completed: true,
        payment_completed_at: result.marked_at,
        payment_manual: true
      }));
      toast.success('Pagamento segnato come completato');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel segnare il pagamento');
    }
    setIsMarkingPaid(false);
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admindea26');
      return;
    }
    
    loadRequest();
    loadWaybillSetting();
  }, [navigate, requestId]);

  const loadWaybillSetting = async () => {
    try {
      const settings = await getAppSettings();
      setAppSettings(settings);
      setWaybillEnabled(!!settings.waybill_feature_enabled);
      setWorkflowConfig({
        exchange: settings.workflow_exchange || { initial_approval: false, final_approval: false },
        voucher: settings.workflow_voucher || { initial_approval: false, final_approval: true },
        rma: settings.workflow_rma || { initial_approval: true, final_approval: true },
      });
    } catch (e) { /* ignore */ }
  };

  const loadEmailLog = useCallback(async () => {
    if (!requestId) return;
    setIsLoadingEmailLog(true);
    try {
      const data = await getEmailLog(requestId);
      setEmailLog(data.email_log || []);
    } catch (e) {
      console.error('[EmailLog] Failed to load:', e);
    }
    setIsLoadingEmailLog(false);
  }, [requestId]);

  const handleResendEmail = async (index, toOverride = null) => {
    setResendingIndex(index);
    try {
      const body = { email_index: index };
      if (toOverride) body.to_override = toOverride;
      await resendEmail(requestId, index, toOverride);
      toast.success(toOverride ? `Email inviata a ${toOverride}` : 'Email reinviata con successo');
      await loadEmailLog();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Errore nel reinvio email');
    }
    setResendingIndex(null);
  };

  const [downloadingPdfIndex, setDownloadingPdfIndex] = useState(null);
  const handleDownloadEmailPdf = async (index) => {
    setDownloadingPdfIndex(index);
    try {
      const response = await api.get(`/admin/returns/${requestId}/email-pdf/${index}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const entry = emailLog[index] || {};
      a.download = `email_${request?.order_number || 'email'}_${entry.type || 'email'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore nel download del PDF');
    }
    setDownloadingPdfIndex(null);
  };

  const handleDownloadReceipt = async () => {
    setIsDownloadingReceipt(true);
    try {
      const response = await downloadReceipt(requestId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const orderNum = request?.order_number || 'ricevuta';
      const reqType = isRMA ? 'rma' : (request?.request_type || 'richiesta');
      a.download = `ricevuta_${orderNum}_${reqType}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Ricevuta scaricata');
    } catch (error) {
      toast.error('Errore nel download della ricevuta');
    }
    setIsDownloadingReceipt(false);
  };

  // Check payment status when request loads (for exchanges with payment)
  useEffect(() => {
    if (request && !isRMA && request.draft_order?.id && request.total_to_pay > 0) {
      loadPaymentStatus();
    }
  }, [request, isRMA, loadPaymentStatus]);

  // Load email log when request is loaded
  useEffect(() => {
    if (request) {
      loadEmailLog();
      loadPendingEmail();
    }
  }, [request, loadEmailLog]);

  const loadPendingEmail = async () => {
    if (!requestId) return;
    try {
      const data = await getPendingEmail(requestId);
      setPendingEmail(data);
    } catch { setPendingEmail(null); }
  };

  const handleSendPendingEmail = async () => {
    setIsSendingPending(true);
    try {
      const result = await sendPendingEmail(requestId);
      toast.success(result.message || 'Email inviata');
      setShowPendingConfirm(false);
      loadEmailLog();
      loadPendingEmail();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Errore nell\'invio');
    }
    setIsSendingPending(false);
  };

  const loadRequest = async () => {
    try {
      // Try to load as RMA first
      try {
        const rmaData = await getRMA(requestId);
        if (rmaData) {
          setRequest(rmaData);
          setIsRMA(true);
          setAdminNotes(rmaData.admin_notes || '');
          setIsLoading(false);
          return;
        }
      } catch (e) {
        // Expected: not an RMA, will try return_requests next
      }
      
      // Try as return request (exchange/voucher)
      const data = await getReturnRequest(requestId);
      setRequest(data);
      setIsRMA(false);
      setAdminNotes(data.admin_notes || '');
    } catch (error) {
      toast.error('Errore nel caricamento della richiesta');
      navigate('/admindea26/resi');
    }
    setIsLoading(false);
  };

  const handleStatusUpdate = async (newStatus) => {
    setIsUpdating(true);
    try {
      const updateFn = isRMA ? updateRMAStatus : updateReturnStatus;
      const updated = await updateFn(requestId, {
        status: newStatus,
        admin_notes: adminNotes
      });
      setRequest(updated);
      toast.success(`Stato aggiornato a: ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
    setIsUpdating(false);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const deleteFn = isRMA ? deleteRMA : deleteReturnRequest;
      await deleteFn(requestId);
      toast.success('Richiesta eliminata. Il cliente può ora fare una nuova richiesta.');
      navigate('/admindea26/resi');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Errore durante l\'eliminazione');
    }
    setIsDeleting(false);
    setShowDeleteConfirm(false);
  };

  const handleManualStatusUpdate = async () => {
    if (!manualNewStatus) {
      toast.error('Seleziona uno stato');
      return;
    }
    setIsUpdating(true);
    try {
      const updateFn = isRMA ? manualStatusUpdateRMA : manualStatusUpdateReturn;
      const payload = {
        status: manualNewStatus,
        admin_notes: adminNotes,
        suppress_email: suppressEmail,
        rejection_reason: manualNewStatus === 'RESO_RIFIUTATO' ? rejectionReason : undefined
      };
      const updated = await updateFn(requestId, payload);
      setRequest(updated);
      setShowManualStatus(false);
      setManualNewStatus('');
      setRejectionReason('');
      toast.success(`Stato aggiornato a: ${MANUAL_STATUS_OPTIONS.find(o => o.value === manualNewStatus)?.label || manualNewStatus}`);
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
    setIsUpdating(false);
  };

  const handleApproveRejectExchange = async (action) => {
    setIsUpdating(true);
    try {
      const payload = {
        action,
        admin_notes: adminNotes,
        production_date_note: action === 'approve' ? productionDateNote : undefined,
        rejection_reason: action === 'reject' ? rejectionReason : undefined
      };
      const updated = await approveRejectExchange(requestId, payload);
      setRequest(updated);
      setApprovalAction('');
      setProductionDateNote('');
      setRejectionReason('');
      toast.success(action === 'approve' ? 'Cambio approvato! Email inviata al cliente.' : 'Richiesta rifiutata. Email inviata al cliente.');
    } catch (error) {
      toast.error('Errore nell\'operazione');
    }
    setIsUpdating(false);
  };

  const handleWorkflowAction = async (action, extraData = {}) => {
    setIsUpdating(true);
    try {
      const payload = {
        action,
        admin_notes: adminNotes,
        rejection_reason: rejectionReason || undefined,
        ...extraData
      };
      const workflowFn = isRMA ? rmaWorkflowAction : returnWorkflowAction;
      const updated = await workflowFn(requestId, payload);
      setRequest(updated);
      setRejectionReason('');
      const actionLabels = {
        approve_send: 'Invio approvato',
        approve_initial: 'Richiesta approvata',
        approve_after_arrival: 'Cambio approvato',
        confirm_arrival: 'Arrivo confermato',
        approve_refund: 'Rimborso approvato',
        confirm_refund_done: 'Rimborso completato',
        reject_refund: 'Reso rifiutato',
        mark_completed: 'Completato',
        approve_voucher: 'Voucher generato e inviato',
        generate_exchange_refund_voucher: 'Voucher rimborso generato e inviato',
        reject: 'Rifiutato'
      };
      toast.success(actionLabels[action] || 'Aggiornato');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Errore nell\'operazione');
    }
    setIsUpdating(false);
  };

  const handleWaybillUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Solo file PDF sono accettati');
      return;
    }
    setIsUploadingWaybill(true);
    try {
      await uploadWaybill(requestId, file);
      toast.success('Lettera di vettura caricata');
      await loadRequest();
      setShowWaybillConfirm(true);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Errore nel caricamento');
    }
    setIsUploadingWaybill(false);
    e.target.value = '';
  };

  const handleSendWaybillEmail = async () => {
    setIsSendingWaybill(true);
    try {
      await sendWaybillEmail(requestId);
      toast.success('Email con lettera di vettura inviata al cliente');
      await loadRequest();
      setShowWaybillConfirm(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Errore nell\'invio email');
    }
    setIsSendingWaybill(false);
  };

  const handleDownloadWaybill = async () => {
    try {
      const response = await downloadWaybill(requestId);
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = request?.waybill?.original_filename || 'lettera_di_vettura.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore nel download');
    }
  };

  const handleNotifyStorePickup = async () => {
    setIsNotifyingStore(true);
    try {
      const res = await notifyStorePickup(requestId, {});
      const data = res.data || res;
      if (data.webhook_sent) {
        toast.success('Notifica inviata al cliente e all\'App Negozio');
      } else {
        toast.success('Notifica email inviata al cliente');
        if (data.webhook_error) {
          toast.warning(`App Negozio non raggiunta: ${data.webhook_error}`, { duration: 6000 });
        }
      }
      await loadRequest();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Errore nell\'invio della notifica');
    }
    setIsNotifyingStore(false);
  };


  // Convert UTC ISO string to Italian time display
  const toItalianTime = (isoString, fmt = 'full') => {
    if (!isoString) return '';
    try {
      const hasTz = /[Zz]|[+-]\d{2}:?\d{2}$/.test(isoString);
      const d = new Date(hasTz ? isoString : isoString + 'Z');
      if (isNaN(d.getTime())) return isoString;
      const opts = fmt === 'short'
        ? { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Rome' }
        : fmt === 'compact'
        ? { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' }
        : { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' };
      return d.toLocaleString('it-IT', opts);
    } catch (e) { return isoString; }
  };

  const formatDate = (dateString) => toItalianTime(dateString, 'full');
  const formatShortDate = (dateString) => toItalianTime(dateString, 'short');

  // Copy customer data
  const copyCustomerData = () => {
    const lines = [
      request?.customer_name || '',
      request?.shipping_address?.address1 || request?.address?.address1 || '',
      [
        request?.shipping_address?.zip || request?.address?.zip || '',
        request?.shipping_address?.city || request?.address?.city || '',
        request?.shipping_address?.province_code || request?.address?.province_code || ''
      ].filter(Boolean).join(' '),
      request?.shipping_address?.country || request?.address?.country || 'Italia',
      request?.customer_phone || '',
      request?.customer_email || ''
    ].filter(Boolean).join('\n');
    
    navigator.clipboard.writeText(lines);
    setCopied(true);
    toast.success('Dati cliente copiati!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Get display type
  const getRequestType = () => {
    if (isRMA) return { label: 'Reso', icon: Package, color: 'bg-amber-500' };
    if (request?.request_type === 'exchange') return { label: 'Cambio', icon: RefreshCw, color: 'bg-blue-500' };
    if (request?.request_type === 'voucher') return { label: 'Voucher', icon: Gift, color: 'bg-emerald-500' };
    return { label: 'Richiesta', icon: Package, color: 'bg-gray-500' };
  };

  const typeConfig = getRequestType();
  const TypeIcon = typeConfig.icon;
  const statusConfig = STATUS_CONFIG[request?.status] || { label: request?.status, color: 'bg-gray-100 text-gray-700' };

  // Get items
  const getItems = () => {
    if (isRMA) return request?.items || [];
    return request?.return_items || [];
  };

  // Calculate totals
  const calculateTotals = () => {
    const items = getItems();
    // Use total_value from database if available, otherwise calculate from items
    const totalValue = request?.total_value || items.reduce((sum, item) => sum + parseFloat(item.price || 0), 0);
    
    if (isRMA) {
      // For RMA: return_cost can be 0 (defect = free return) or 35 (standard)
      // Only use default 35 if return_cost is null/undefined, NOT if it's 0
      const returnCost = request?.return_cost !== undefined && request?.return_cost !== null 
        ? request.return_cost 
        : 35;
      // refund_amount from DB, or calculate it
      const refund = request?.refund_amount !== undefined && request?.refund_amount !== null
        ? request.refund_amount
        : (totalValue - returnCost);
      return { totalValue, returnCost, refund };
    } else {
      // exchange_fee can be 0 (store delivery), so check for null/undefined explicitly
      const exchangeFee = request?.exchange_fee !== undefined && request?.exchange_fee !== null 
        ? request.exchange_fee 
        : 20;
      return { 
        totalValue, 
        exchangeFee, 
        totalToPay: request?.total_to_pay || 0,
        refund: request?.refund_amount || 0
      };
    }
  };

  const totals = request ? calculateTotals() : {};

  // Check if payment is completed (from multiple sources)
  const isPaymentCompleted = request?.payment_completed || 
                             paymentStatus?.is_paid || 
                             request?.draft_order?.status === 'paid';

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8 bg-gray-50 min-h-screen">
      {/* Back Link */}
      <Link 
        to="/admindea26/resi" 
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-primary mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Torna ai Resi
      </Link>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl lg:text-2xl font-heading font-semibold text-gray-900">
            #{request?.order_number}
          </h1>
          <span className={cn(
            "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs sm:text-sm font-medium text-white",
            typeConfig.color
          )}>
            <TypeIcon className="w-4 h-4" />
            {typeConfig.label}
          </span>
          <span className={cn(
            "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs sm:text-sm font-medium border",
            statusConfig.color
          )}>
            <Clock className="w-3.5 h-3.5" />
            {statusConfig.label}
          </span>
        </div>
        
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">Creato: {formatShortDate(request?.created_at)}</span>
          <Button
            onClick={handleDownloadReceipt}
            disabled={isDownloadingReceipt}
            variant="outline"
            size="sm"
            className="h-8 text-gray-600 border-gray-300 hover:bg-gray-50"
            data-testid="download-receipt-btn"
          >
            {isDownloadingReceipt ? (
              <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
            ) : (
              <FileDown className="w-4 h-4 mr-1.5" />
            )}
            Ricevuta
          </Button>
        </div>
      </div>

      {/* Logistics Alert Banner */}
      {request?.logistics_alert?.active && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-600 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 shadow-lg"
          data-testid="logistics-alert-banner"
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-white font-bold text-sm">PACCO DA RIFIUTARE</p>
              <p className="text-red-100 text-xs sm:text-sm">{request.logistics_alert.message}</p>
            </div>
          </div>
          <div className="flex-shrink-0 bg-white/20 rounded-lg px-3 py-1.5 self-end sm:self-auto">
            <span className="text-white font-bold text-lg">{request.logistics_alert.days_elapsed}gg</span>
          </div>
        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Articoli */}
        <div className="lg:col-span-2 space-y-6">
          {/* Articoli Restituiti */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <Package className="w-5 h-5 text-gray-400" />
                Articoli restituiti
              </h2>
              
              {/* Delete button (for active statuses only) */}
              {['pending', 'requested', 'RICHIESTA', 'RICHIESTA_DA_VERIFICARE', 'IN_ATTESA_DI_ARRIVO', 'CAMBIO_IN_LAVORAZIONE'].includes(request?.status) && (
                <Button 
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isUpdating || isDeleting}
                  size="sm"
                  variant="outline"
                  className="border-gray-300 text-gray-600 hover:bg-gray-50 h-9"
                  data-testid="delete-request-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Elimina
                </Button>
              )}
            </div>
            
            <div className="divide-y divide-gray-100">
              {getItems().map((item, i) => (
                <div key={item.variant_id || item.line_item_id || `item-${i}`} className="p-6 flex gap-4">
                  <div className="w-24 h-28 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-8 h-8 text-gray-300" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-sm text-gray-500 mb-2">{item.variant_title}</p>
                    
                    {/* Reason */}
                    {isRMA && item.q3_return_reason && (
                      <p className="text-sm text-gray-600">
                        <span className="text-gray-400">Motivo del reso:</span>{' '}
                        <span className="font-medium">{REASON_LABELS[item.q3_return_reason] || item.q3_return_reason}</span>
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold text-gray-900">€{parseFloat(item.price).toFixed(2)}</p>
                    <p className="text-sm text-gray-400">x1</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Exchange Items */}
          {!isRMA && request?.exchange_items?.length > 0 && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl border border-emerald-200 overflow-hidden"
            >
              <div className="px-6 py-4 border-b border-emerald-100 bg-emerald-50">
                <h2 className="font-semibold text-emerald-800 flex items-center gap-2">
                  <RefreshCw className="w-5 h-5" />
                  Nuovi Articoli Richiesti
                </h2>
              </div>
              <div className="divide-y divide-emerald-100">
                {request.exchange_items.map((item, i) => (
                  <div key={item.variant_id || `exchange-${i}`} className="p-6 flex gap-4">
                    <div className="w-24 h-28 bg-emerald-50 rounded-lg overflow-hidden flex-shrink-0">
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-8 h-8 text-emerald-200" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-500">{item.variant_title}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-emerald-700">€{parseFloat(item.price).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Defect Photos */}
          {isRMA && request?.defect_photos?.length > 0 && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-white rounded-xl border border-red-200 overflow-hidden"
            >
              <div className="px-6 py-4 border-b border-red-100 bg-red-50">
                <h2 className="font-semibold text-red-800 flex items-center gap-2">
                  <Camera className="w-5 h-5" />
                  Foto Difetto
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-4 gap-4">
                  {request.defect_photos.map((photo, i) => (
                    <a 
                      key={`photo-${i}-${photo.slice(-10)}`} 
                      href={photo} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="aspect-square bg-gray-100 rounded-lg overflow-hidden hover:opacity-80 transition-opacity"
                    >
                      <img src={photo} alt={`Difetto ${i + 1}`} className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Riepilogo Economico - Dettagliato */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-gray-400" />
                Riepilogo Economico
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {/* Valore articoli restituiti */}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Valore articoli restituiti</span>
                  <span className="text-gray-900 font-medium">€{totals.totalValue?.toFixed(2)}</span>
                </div>
                
                {/* === VOUCHER REQUEST === */}
                {!isRMA && request?.request_type === 'voucher' && (
                  <>
                    {request?.paid_with_gift_card ? (
                      /* Gift card order: show deduction */
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Costi di gestione reso</span>
                        <span className="text-red-600 font-medium">
                          -€{(totals.totalValue - (request?.voucher_value || 0)).toFixed(2)}
                        </span>
                      </div>
                    ) : request?.voucher_value && request.voucher_value > totals.totalValue ? (
                      /* Normal order with bonus */
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Bonus voucher</span>
                        <span className="text-emerald-600 font-medium">
                          +€{(request.voucher_value - totals.totalValue).toFixed(2)}
                        </span>
                      </div>
                    ) : null}
                    <div className="border-t border-gray-200 pt-4">
                      <div className={cn(
                        "border rounded-lg p-4",
                        request?.paid_with_gift_card 
                          ? "bg-amber-50 border-amber-200" 
                          : "bg-emerald-50 border-emerald-200"
                      )}>
                        <div className="flex items-center gap-3 mb-2">
                          <Gift className={cn("w-6 h-6", request?.paid_with_gift_card ? "text-amber-600" : "text-emerald-600")} />
                          <span className={cn("font-semibold", request?.paid_with_gift_card ? "text-amber-800" : "text-emerald-800")}>
                            Voucher da generare
                          </span>
                          {request?.paid_with_gift_card && (
                            <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">Gift Card</span>
                          )}
                        </div>
                        <p className={cn("text-3xl font-bold", request?.paid_with_gift_card ? "text-amber-700" : "text-emerald-700")}>
                          €{(request?.voucher_value || totals.totalValue)?.toFixed(2)}
                        </p>
                        <p className={cn("text-sm mt-2", request?.paid_with_gift_card ? "text-amber-600" : "text-emerald-600")}>
                          {request?.status === 'approved' || request?.gift_card 
                            ? '✓ Voucher già generato' 
                            : 'Il voucher verrà generato all\'approvazione'
                          }
                        </p>
                      </div>
                    </div>
                  </>
                )}

                {/* === RMA (RESO) === */}
                {isRMA && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Costi di gestione reso</span>
                      <span className="text-red-600">-€{totals.returnCost?.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-gray-200 pt-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <Wallet className="w-6 h-6 text-blue-600" />
                          <span className="font-semibold text-blue-800">Rimborso da erogare</span>
                        </div>
                        <p className="text-3xl font-bold text-blue-700">€{totals.refund?.toFixed(2)}</p>
                      </div>
                    </div>
                  </>
                )}

                {/* === EXCHANGE (CAMBIO) === */}
                {!isRMA && request?.request_type === 'exchange' && (
                  <>
                    {/* Valore nuovi articoli */}
                    {request?.exchange_items?.length > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Valore nuovi articoli</span>
                        <span className="text-gray-900 font-medium">
                          €{request.exchange_items.reduce((sum, item) => sum + parseFloat(item.price || 0), 0).toFixed(2)}
                        </span>
                      </div>
                    )}

                    {/* Differenza prezzo */}
                    {request?.price_difference !== undefined && request?.price_difference !== 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">
                          {request.price_difference > 0 ? 'Differenza prezzo' : 'Differenza (a favore cliente)'}
                        </span>
                        <span className={cn(
                          "font-medium",
                          request.price_difference > 0 ? "text-amber-600" : "text-emerald-600"
                        )}>
                          {request.price_difference > 0 ? '+' : ''}€{request.price_difference?.toFixed(2)}
                        </span>
                      </div>
                    )}

                    {/* Metodo consegna */}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Metodo consegna</span>
                      <span className={cn(
                        "font-medium",
                        request?.delivery_method === 'store' ? "text-emerald-600" : "text-gray-900"
                      )}>
                        {(() => {
                          const dm = request?.delivery_method;
                          if (dm === 'store') return 'Consegna in negozio';
                          const labels = appSettings?.delivery_method_labels || {};
                          const method = dm || 'pickup';
                          return labels[`${method}_label`] || (method === 'pickup' ? 'Ritiro a domicilio' : method === 'label' ? 'Spedizione autonoma' : 'Spedizione guidata');
                        })()}
                      </span>
                    </div>

                    {/* Costi gestione */}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Costi di gestione cambio</span>
                      <span className={cn(
                        request?.exchange_fee === 0 ? "text-emerald-600 font-medium" : "text-gray-900"
                      )}>
                        {request?.exchange_fee === 0 ? '€0.00 (consegna in negozio)' : `€${totals.exchangeFee?.toFixed(2)}`}
                      </span>
                    </div>

                    <div className="border-t border-gray-200 pt-4 space-y-4">
                      {/* CASO 1: Cliente deve pagare */}
                      {totals.totalToPay > 0 && (
                        <div className={cn(
                          "border rounded-lg p-4",
                          isPaymentCompleted
                            ? "bg-emerald-50 border-emerald-200"
                            : "bg-amber-50 border-amber-200"
                        )}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              {isPaymentCompleted ? (
                                <CheckCircle className="w-6 h-6 text-emerald-600" />
                              ) : (
                                <CreditCard className="w-6 h-6 text-amber-600" />
                              )}
                              <span className={cn(
                                "font-semibold",
                                isPaymentCompleted
                                  ? "text-emerald-800"
                                  : "text-amber-800"
                              )}>
                                {isPaymentCompleted
                                  ? 'Pagamento ricevuto'
                                  : 'Da pagare dal cliente'
                                }
                              </span>
                            </div>
                            <p className={cn(
                              "text-2xl font-bold",
                              isPaymentCompleted
                                ? "text-emerald-700"
                                : "text-amber-700"
                            )}>
                              €{totals.totalToPay?.toFixed(2)}
                            </p>
                          </div>
                          
                          {/* Status e azioni pagamento */}
                          {!(isPaymentCompleted) && (
                            <div className="mt-3 flex flex-wrap items-center gap-3">
                              <Button
                                onClick={loadPaymentStatus}
                                disabled={isCheckingPayment}
                                variant="outline"
                                size="sm"
                                className="text-amber-700 border-amber-300 hover:bg-amber-100"
                                data-testid="check-payment-btn"
                              >
                                {isCheckingPayment ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Verifico...
                                  </>
                                ) : (
                                  <>
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Verifica pagamento
                                  </>
                                )}
                              </Button>
                              <Button
                                onClick={handleMarkAsPaid}
                                disabled={isMarkingPaid}
                                variant="default"
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid="mark-as-paid-btn"
                              >
                                {isMarkingPaid ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Aggiorno...
                                  </>
                                ) : (
                                  <>
                                    <CreditCard className="w-4 h-4 mr-2" />
                                    Segna come pagato
                                  </>
                                )}
                              </Button>
                              {request?.draft_order?.invoice_url && (
                                <a 
                                  href={request.draft_order.invoice_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-sm text-amber-600 hover:text-amber-800 flex items-center gap-1"
                                >
                                  Vedi fattura <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                          )}
                          
                          {(isPaymentCompleted) && request?.payment_completed_at && (
                            <p className="text-sm text-emerald-600 mt-2">
                              Pagato il {toItalianTime(request.payment_completed_at, 'full')}
                              {request?.payment_manual && (
                                <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                                  Manuale
                                </span>
                              )}
                            </p>
                          )}
                        </div>
                      )}

                      {/* CASO 2: Cliente riceve rimborso/voucher */}
                      {totals.refund > 0 && (
                        <div className={cn(
                          "border rounded-lg p-4",
                          request?.refund_preference === 'voucher'
                            ? "bg-emerald-50 border-emerald-200"
                            : "bg-blue-50 border-blue-200"
                        )}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              {request?.refund_preference === 'voucher' ? (
                                <Gift className="w-6 h-6 text-emerald-600" />
                              ) : (
                                <Wallet className="w-6 h-6 text-blue-600" />
                              )}
                              <span className={cn(
                                "font-semibold",
                                request?.refund_preference === 'voucher'
                                  ? "text-emerald-800"
                                  : "text-blue-800"
                              )}>
                                {request?.refund_preference === 'voucher'
                                  ? 'Buono da generare (+10%)'
                                  : 'Rimborso da erogare'
                                }
                              </span>
                            </div>
                            <p className={cn(
                              "text-2xl font-bold",
                              request?.refund_preference === 'voucher'
                                ? "text-emerald-700"
                                : "text-blue-700"
                            )}>
                              €{request?.refund_preference === 'voucher'
                                ? (request?.credit_voucher_value || totals.refund * 1.10).toFixed(2)
                                : totals.refund?.toFixed(2)
                              }
                            </p>
                          </div>
                          
                          {request?.refund_preference && (
                            <p className={cn(
                              "text-sm mt-2",
                              request?.refund_preference === 'voucher'
                                ? "text-emerald-600"
                                : "text-blue-600"
                            )}>
                              {request?.refund_preference === 'voucher'
                                ? `Cliente ha scelto buono acquisto (base €${totals.refund?.toFixed(2)} + 10%)`
                                : 'Cliente ha scelto rimborso monetario'
                              }
                            </p>
                          )}
                        </div>
                      )}

                      {/* CASO 3: Nessun pagamento/rimborso (pari) */}
                      {totals.totalToPay === 0 && totals.refund === 0 && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <Check className="w-6 h-6 text-gray-600" />
                            <span className="font-semibold text-gray-800">Nessun pagamento richiesto</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-2">
                            Il valore dei nuovi articoli corrisponde a quello degli articoli restituiti
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right Column - Cliente & Azioni */}
        <div className="space-y-6">
          {/* Dati Cliente */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <User className="w-5 h-5 text-gray-400" />
                Cliente
              </h2>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={copyCustomerData}
                className="h-8 text-gray-500 hover:text-primary"
              >
                {copied ? (
                  <Check className="w-4 h-4 mr-1 text-emerald-500" />
                ) : (
                  <Copy className="w-4 h-4 mr-1" />
                )}
                {copied ? 'Copiato!' : 'Copia'}
              </Button>
            </div>
            <div className="p-6">
              {/* Customer data in shipping label format */}
              <div className="space-y-1 text-sm text-gray-700">
                <p className="font-semibold text-gray-900">{request?.customer_name}</p>
                {(request?.shipping_address || request?.address) && (
                  <>
                    <p>{request?.shipping_address?.address1 || request?.address?.address1}</p>
                    <p>
                      {request?.shipping_address?.zip || request?.address?.zip}{' '}
                      {request?.shipping_address?.city || request?.address?.city}{' '}
                      {request?.shipping_address?.province_code || request?.address?.province_code}
                    </p>
                    <p>{request?.shipping_address?.country || request?.address?.country || 'Italia'}</p>
                  </>
                )}
                {request?.customer_phone && (
                  <p>{request?.customer_phone}</p>
                )}
                <p>{request?.customer_email}</p>
                {request?.override_email && (
                  <p className="text-xs text-blue-600 font-medium mt-1">
                    Invio a: {request.override_email}
                  </p>
                )}
                {request?.paid_with_gift_card && (
                  <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded-full border border-amber-300" data-testid="gift-card-badge">
                    <CreditCard className="w-3.5 h-3.5" />
                    Pagato con Gift Card
                  </div>
                )}
              </div>
              
              {request?.iban && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-400 mb-1">IBAN</p>
                  <p className="font-mono text-sm text-gray-900">{request.iban}</p>
                </div>
              )}
              
              {/* Override Email */}
              <OverrideEmailField requestId={requestId} currentEmail={request?.override_email} onUpdate={loadRequest} />
            </div>
          </motion.div>

          {/* ===== EMAIL DA INVIARE ===== */}
          {pendingEmail && (pendingEmail.has_pending || pendingEmail.already_sent) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.04 }}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              data-testid="pending-email-section"
            >
              <div className="px-6 py-4 border-b border-gray-100">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <MailPlus className="w-5 h-5 text-amber-500" />
                  Email da Inviare
                </h2>
              </div>
              <div className="p-4">
                <div className={cn(
                  "flex items-center justify-between p-3 rounded-lg border",
                  pendingEmail.already_sent
                    ? "bg-emerald-50 border-emerald-200"
                    : "bg-amber-50 border-amber-200"
                )}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={cn(
                      "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
                      pendingEmail.already_sent ? "bg-emerald-100" : "bg-amber-100"
                    )}>
                      <Mail className={cn("w-4 h-4", pendingEmail.already_sent ? "text-emerald-600" : "text-amber-600")} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">{pendingEmail.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {pendingEmail.already_sent
                          ? 'Questa email è già stata inviata'
                          : `Email prevista per lo stato attuale`
                        }
                      </p>
                    </div>
                  </div>
                  {pendingEmail.has_pending && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowPendingConfirm(true)}
                      className="flex items-center gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-100 flex-shrink-0 ml-3"
                      data-testid="send-pending-email-btn"
                    >
                      <Send className="w-3.5 h-3.5" />
                      Invia
                    </Button>
                  )}
                  {pendingEmail.already_sent && (
                    <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium flex-shrink-0 ml-3">
                      <CheckCircle className="w-3.5 h-3.5" />
                      Inviata
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Pending email confirm dialog */}
          {showPendingConfirm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="pending-email-confirm-dialog">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Invia email</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Vuoi inviare l'email <strong>"{pendingEmail?.label}"</strong> al cliente{' '}
                  <strong>{request?.customer_email}</strong>?
                </p>
                {pendingEmail?.needs_gift_card && !request?.gift_card && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 mb-4">
                    <p className="text-xs text-amber-700">Verrà generato automaticamente un voucher Shopify prima dell'invio.</p>
                  </div>
                )}
                <div className="flex items-center justify-end gap-3">
                  <Button variant="outline" onClick={() => setShowPendingConfirm(false)} disabled={isSendingPending} data-testid="pending-cancel-btn">
                    Annulla
                  </Button>
                  <Button onClick={handleSendPendingEmail} disabled={isSendingPending} data-testid="pending-confirm-btn">
                    {isSendingPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    {isSendingPending ? 'Invio...' : 'Conferma invio'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* ===== EMAIL LOG ===== */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <Send className="w-5 h-5 text-gray-400" />
                Email Inviate
              </h2>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={loadEmailLog}
                disabled={isLoadingEmailLog}
                className="h-8 text-gray-500 hover:text-primary"
                data-testid="refresh-email-log-btn"
              >
                <RotateCcw className={cn("w-4 h-4", isLoadingEmailLog && "animate-spin")} />
              </Button>
            </div>
            <div className="p-4">
              {isLoadingEmailLog && emailLog.length === 0 ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : emailLog.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-3" data-testid="no-emails-msg">Nessuna email registrata</p>
              ) : (
                <div className="space-y-2">
                  {emailLog.map((entry, i) => {
                    const EMAIL_TYPE_LABELS = {
                      'conferma_rma': 'Conferma RMA',
                      'conferma_exchange': 'Conferma Cambio',
                      'conferma_voucher': 'Conferma Voucher',
                      'approvazione_invio_rma': 'Approvazione Invio',
                      'voucher_approvazione_iniziale': 'Approvazione Iniziale',
                      'voucher_approvato': 'Voucher Approvato',
                      'voucher_approvato_manuale': 'Voucher Approvato',
                      'voucher_approvato_auto': 'Voucher Auto-Approvato',
                      'voucher_approvato_workflow': 'Voucher Approvato',
                      'voucher_rimborso_cambio': 'Voucher Rimborso',
                      'voucher_rimborso_auto': 'Voucher Rimborso Auto',
                      'cambio_approvato': 'Cambio Approvato',
                      'cambio_rifiutato': 'Cambio Rifiutato',
                      'cambio_in_lavorazione': 'Cambio in Lavorazione',
                      'cambio_in_lavorazione_approvato': 'Cambio in Lavorazione',
                      'rimborso_approvato': 'Rimborso Approvato',
                      'rimborso_approvato_auto': 'Rimborso Auto-Approvato',
                      'rma_rifiutato': 'RMA Rifiutato',
                      'richiesta_rifiutata': 'Richiesta Rifiutata',
                      'lettera_di_vettura': 'Lettera di Vettura',
                      'notifica_ritiro_negozio': 'Ritiro in Negozio',
                    };
                    // Handle reinvio_ prefix
                    const typeKey = entry.type?.startsWith('reinvio_') ? entry.type.replace('reinvio_', '') : entry.type;
                    const isResend = entry.type?.startsWith('reinvio_');
                    const label = EMAIL_TYPE_LABELS[typeKey] || entry.type;
                    
                    let sentDate = '';
                    try {
                      sentDate = toItalianTime(entry.sent_at, 'compact');
                    } catch (e) { sentDate = entry.sent_at; }
                    
                    return (
                      <div 
                        key={`${entry.type}-${entry.sent_at}-${i}`}
                        className={cn(
                          "flex items-center gap-3 p-2.5 rounded-lg text-sm border",
                          entry.success 
                            ? "bg-emerald-50 border-emerald-200" 
                            : "bg-red-50 border-red-200"
                        )}
                        data-testid={`email-log-entry-${i}`}
                      >
                        <div className={cn(
                          "w-2 h-2 rounded-full flex-shrink-0",
                          entry.success ? "bg-emerald-500" : "bg-red-500"
                        )} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-gray-900 truncate">
                              {label}
                            </span>
                            {isResend && (
                              <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full flex-shrink-0">reinvio</span>
                            )}
                            {entry.suppressed && (
                              <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full flex-shrink-0">test mode</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                            <span>{sentDate}</span>
                            {entry.error && (
                              <span className="text-red-600 truncate" title={entry.error}>
                                {entry.error.length > 40 ? entry.error.slice(0, 40) + '...' : entry.error}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {/* Download PDF */}
                          {entry.has_pdf && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadEmailPdf(i)}
                              disabled={downloadingPdfIndex !== null}
                              className="h-7 px-2 text-gray-600 hover:bg-gray-100"
                              title="Scarica PDF email"
                              data-testid={`download-email-pdf-${i}`}
                            >
                              {downloadingPdfIndex === i ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <FileDown className="w-3.5 h-3.5" />
                              )}
                            </Button>
                          )}
                          {/* Resend to override email */}
                          {request?.override_email && entry.success && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResendEmail(i, request.override_email)}
                              disabled={resendingIndex !== null}
                              className="h-7 px-2 text-blue-700 hover:bg-blue-100"
                              title={`Invia a ${request.override_email}`}
                              data-testid={`resend-override-btn-${i}`}
                            >
                              {resendingIndex === i ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Send className="w-3.5 h-3.5" />
                              )}
                            </Button>
                          )}
                          {/* Retry failed emails */}
                          {!entry.success && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResendEmail(i)}
                              disabled={resendingIndex !== null}
                              className="h-7 px-2 text-red-700 hover:bg-red-100"
                              data-testid={`resend-email-btn-${i}`}
                            >
                              {resendingIndex === i ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <RotateCcw className="w-3.5 h-3.5" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>

          {/* ===== FLOW-SPECIFIC WORKFLOW ACTIONS ===== */}
          
          {/* --- CAMBIO: RICHIESTA (waiting for payment or initial approval) --- */}
          {request?.request_type === 'exchange' && request?.status === 'RICHIESTA' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-amber-50 rounded-xl border border-amber-200 p-5"
            >
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800">In attesa di pagamento</p>
                  <p className="text-sm text-amber-700 mt-1">
                    L'ordine bozza è stato inviato al cliente. In attesa del pagamento 
                    ({request?.is_preorder ? '12 ore' : '2 ore'} di scadenza).
                  </p>
                  {request?.draft_order?.invoice_url && (
                    <a href={request.draft_order.invoice_url} target="_blank" rel="noopener noreferrer" 
                       className="inline-flex items-center gap-1 text-xs text-amber-700 underline mt-2">
                      <ExternalLink className="w-3 h-3" /> Link pagamento
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* --- CAMBIO: Draft order non completato (solo se completamento automatico fallito) --- */}
          {request?.request_type === 'exchange' && request?.draft_order?.id && 
           (request?.draft_order?.completion_failed || 
            request?.draft_order?.status === 'draft_created_completion_failed') && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-red-50 rounded-xl border border-red-200 p-5"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-red-800">Ordine Shopify non completato</p>
                  <p className="text-sm text-red-700 mt-1">
                    La bozza d'ordine #{request.draft_order.name || request.draft_order.id} non è stata convertita in ordine reale. Clicca per completare.
                  </p>
                  <Button
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('admin_token');
                        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/returns/${request.id}/complete-draft`, {
                          method: 'PUT',
                          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({})
                        });
                        if (res.ok) {
                          toast.success('Ordine completato su Shopify!');
                          loadRequest();
                        } else {
                          const data = await res.json();
                          toast.error(data.detail || 'Errore nel completamento');
                        }
                      } catch (e) { toast.error('Errore di connessione: ' + e.message); }
                    }}
                    className="mt-3 bg-red-600 hover:bg-red-700 text-white"
                    data-testid="complete-draft-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Completa Ordine su Shopify
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* --- VOUCHER: RICHIESTA (initial_approval abilitato) --- */}
          {request?.request_type === 'voucher' && request?.status === 'RICHIESTA' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-amber-50 rounded-xl border border-amber-200 p-6"
            >
              <h3 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Voucher — In Attesa di Approvazione
              </h3>
              <p className="text-sm text-amber-700 mb-4">Approva questa richiesta per permettere al cliente di spedire il pacco.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={() => handleWorkflowAction('approve_initial')} disabled={isUpdating}
                  className="flex-1 bg-amber-600 hover:bg-amber-700" data-testid="approve-voucher-initial-btn">
                  {isUpdating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                  Approva Invio
                </Button>
                <Button onClick={() => setApprovalAction('reject_initial')} disabled={isUpdating}
                  variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid="reject-voucher-initial-btn">
                  <X className="w-4 h-4 mr-2" /> Rifiuta
                </Button>
              </div>
              {approvalAction === 'reject_initial' && (
                <div className="space-y-3 mt-4">
                  <Textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Motivo del rifiuto..." className="min-h-[80px] resize-none" data-testid="voucher-initial-rejection-reason" />
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button onClick={() => { setApprovalAction(''); setRejectionReason(''); }} variant="outline" className="flex-1">Annulla</Button>
                    <Button onClick={() => handleWorkflowAction('reject')} disabled={isUpdating || !rejectionReason}
                      className="flex-1 bg-red-600 hover:bg-red-700" data-testid="confirm-reject-voucher-initial-btn">
                      {isUpdating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <X className="w-4 h-4 mr-2" />}
                      Conferma Rifiuto
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* --- CAMBIO: IN_ATTESA_DI_ARRIVO (solo ritiro a domicilio) --- */}
          {request?.request_type === 'exchange' && request?.status === 'IN_ATTESA_DI_ARRIVO' && request?.delivery_method !== 'store' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-blue-50 rounded-xl border border-blue-200 p-6"
            >
              <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-500" />
                {request?.arrival_confirmed_at && workflowConfig.exchange?.final_approval
                  ? 'Pacco Arrivato — Approvazione Finale'
                  : 'In Attesa di Arrivo'}
              </h3>
              <p className="text-sm text-blue-700 mb-4">
                {request?.arrival_confirmed_at && workflowConfig.exchange?.final_approval
                  ? 'Il pacco è arrivato. Controlla la merce e approva per procedere con il cambio.'
                  : 'Il pacco del cliente è in arrivo. Conferma l\'arrivo per procedere o rifiuta il reso.'}
              </p>

              {/* Caso: pacco non ancora arrivato OPPURE final_approval disabilitato */}
              {!request?.arrival_confirmed_at && !approvalAction && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button onClick={() => handleWorkflowAction('confirm_arrival')} disabled={isUpdating}
                    className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="confirm-arrival-btn">
                    {isUpdating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                    Conferma Arrivo Pacco
                  </Button>
                  <Button onClick={() => setApprovalAction('reject_exchange')} disabled={isUpdating}
                    variant="outline" className="flex-1 border-red-300 text-red-600 hover:bg-red-50" data-testid="reject-exchange-arrival-btn">
                    <X className="w-4 h-4 mr-2" /> Rifiuta Pacco
                  </Button>
                </div>
              )}

              {/* Caso: pacco arrivato + final_approval abilitato */}
              {request?.arrival_confirmed_at && workflowConfig.exchange?.final_approval && !approvalAction && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button onClick={() => handleWorkflowAction('approve_after_arrival')} disabled={isUpdating}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="approve-exchange-final-btn">
                    {isUpdating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                    Approva Cambio
                  </Button>
                  <Button onClick={() => setApprovalAction('reject_exchange')} disabled={isUpdating}
                    variant="outline" className="flex-1 border-red-300 text-red-600 hover:bg-red-50" data-testid="reject-exchange-final-btn">
                    <X className="w-4 h-4 mr-2" /> Rifiuta
                  </Button>
                </div>
              )}

              {/* Rejection reason form */}
              {approvalAction === 'reject_exchange' && (
                <div className="space-y-3 mt-4">
                  <Textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Motivo del rifiuto..." className="min-h-[80px] resize-none" data-testid="exchange-rejection-reason" />
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button onClick={() => { setApprovalAction(''); setRejectionReason(''); }} variant="outline" className="flex-1">Annulla</Button>
                    <Button onClick={() => handleWorkflowAction('reject')} disabled={isUpdating || !rejectionReason}
                      className="flex-1 bg-red-600 hover:bg-red-700" data-testid="confirm-reject-exchange-btn">
                      {isUpdating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <X className="w-4 h-4 mr-2" />}
                      Conferma Rifiuto
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* --- CAMBIO: CAMBIO_IN_LAVORAZIONE → Verifica evasione automatica da Shopify --- */}
          {request?.request_type === 'exchange' && request?.status === 'CAMBIO_IN_LAVORAZIONE' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-purple-50 rounded-xl border border-purple-200 p-6"
            >
              <h3 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-purple-500" />
                Cambio in Lavorazione
              </h3>
              <p className="text-sm text-purple-700 mb-3">Il pacco del cliente è arrivato. Evadi l'ordine di cambio su Shopify come un ordine normale.</p>
              <div className="flex items-center gap-2 px-3 py-2 bg-purple-100 rounded-lg mb-4" data-testid="fulfillment-auto-check-info">
                <Clock className="w-4 h-4 text-purple-600 flex-shrink-0" />
                <p className="text-xs text-purple-700">Lo stato si aggiornerà automaticamente a <strong>Completato</strong> quando l'ordine risulterà evaso su Shopify (webhook).</p>
              </div>
              <Button
                onClick={async () => {
                  setIsUpdating(true);
                  try {
                    const data = await checkFulfillmentStatus(request.id);
                    if (data.status === 'completed') {
                      toast.success(data.message);
                      loadRequest();
                    } else if (data.status === 'not_fulfilled') {
                      toast.warning(data.message);
                    } else if (data.status === 'error') {
                      toast.error(data.message);
                    } else {
                      toast.info(data.message);
                    }
                  } catch (err) {
                    const msg = err?.response?.data?.detail || err?.message || 'Errore di connessione al server';
                    toast.error(msg);
                  }
                  setIsUpdating(false);
                }}
                disabled={isUpdating}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="check-fulfillment-btn"
              >
                {isUpdating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Verifica Evasione su Shopify
              </Button>
            </motion.div>
          )}

          {/* --- CAMBIO CON RIMBORSO: Genera Voucher per differenza a favore del cliente --- */}
          {request?.request_type === 'exchange' && request?.refund_amount > 0 && (
            request?.status === 'CAMBIO_IN_LAVORAZIONE' || 
            request?.status === 'IN_ATTESA_DI_ARRIVO' ||
            request?.status === 'RICHIESTA' ||
            request?.status === 'COMPLETATO'
          ) && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className="bg-emerald-50 rounded-xl border border-emerald-200 p-6"
            >
              <h3 className="font-semibold text-emerald-900 mb-3 flex items-center gap-2">
                <Gift className="w-5 h-5 text-emerald-500" />
                Voucher Rimborso Cambio
              </h3>

              {!request?.exchange_refund_gift_card ? (
                <>
                  <p className="text-sm text-emerald-700 mb-2">
                    Il cliente ha diritto a un rimborso di <strong>€{request.refund_amount.toFixed(2)}</strong> poiché 
                    il prodotto scelto per il cambio ha un valore inferiore.
                  </p>
                  <p className="text-sm text-emerald-600 mb-4">
                    Genera un voucher Shopify per completare la richiesta.
                  </p>
                  <div className="flex items-center gap-3 p-3 bg-emerald-100 rounded-lg mb-4">
                    <Gift className="w-6 h-6 text-emerald-600" />
                    <div>
                      <p className="text-lg font-bold text-emerald-800">€{request.refund_amount.toFixed(2)}</p>
                      <p className="text-xs text-emerald-600">Voucher da generare</p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => handleWorkflowAction('generate_exchange_refund_voucher')} 
                    disabled={isUpdating}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    data-testid="generate-exchange-refund-voucher-btn"
                  >
                    {isUpdating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Gift className="w-4 h-4 mr-2" />}
                    Genera Voucher Rimborso €{request.refund_amount.toFixed(2)}
                  </Button>
                </>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 p-3 bg-emerald-100 rounded-lg" data-testid="exchange-refund-voucher-generated">
                    <Check className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="font-semibold text-emerald-800">Voucher Generato</p>
                      <p className="text-sm text-emerald-600">
                        Codice: <span className="font-mono font-bold">{request.exchange_refund_gift_card.code}</span>
                      </p>
                      <p className="text-sm text-emerald-600">
                        Valore: €{request.exchange_refund_gift_card.amount || request.refund_amount.toFixed(2)}
                      </p>
                      {request.exchange_refund_gift_card.expires_on && (
                        <p className="text-xs text-emerald-500">Scade il: {request.exchange_refund_gift_card.expires_on}</p>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-emerald-600">Il voucher è stato inviato via email al cliente.</p>
                </div>
              )}
            </motion.div>
          )}

          {/* --- VOUCHER: IN_ATTESA_DI_ARRIVO (solo ritiro a domicilio) --- */}
          {request?.request_type === 'voucher' && request?.status === 'IN_ATTESA_DI_ARRIVO' && request?.delivery_method !== 'store' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-emerald-50 rounded-xl border border-emerald-200 p-6"
            >
              <h3 className="font-semibold text-emerald-900 mb-3 flex items-center gap-2">
                <Gift className="w-5 h-5 text-emerald-500" />
                {request?.arrival_confirmed_at && workflowConfig.voucher?.final_approval
                  ? 'Pacco Arrivato — Approva Voucher'
                  : workflowConfig.voucher?.final_approval
                    ? 'In Attesa di Arrivo — Voucher'
                    : 'In Attesa di Arrivo — Voucher'}
              </h3>
              <p className="text-sm text-emerald-700 mb-2">
                {request?.arrival_confirmed_at && workflowConfig.voucher?.final_approval
                  ? 'Il pacco è arrivato. Controlla e approva la generazione del voucher.'
                  : workflowConfig.voucher?.final_approval
                    ? 'Il pacco del cliente è in arrivo. Conferma l\'arrivo, poi approva la generazione del voucher.'
                    : 'Il pacco del cliente è in arrivo. Conferma l\'arrivo per generare automaticamente il voucher.'}
              </p>
              {request?.voucher_value && (
                <p className="text-sm font-semibold text-emerald-800 mb-4">Valore voucher: €{request.voucher_value.toFixed(2)}</p>
              )}

              {/* Pacco non ancora arrivato */}
              {!request?.arrival_confirmed_at && (
                <div className="flex flex-col sm:flex-row gap-2">
                  {workflowConfig.voucher?.final_approval ? (
                    <Button onClick={() => handleWorkflowAction('confirm_arrival')} disabled={isUpdating}
                      className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="voucher-confirm-arrival-btn">
                      {isUpdating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Package className="w-4 h-4 mr-2" />}
                      Conferma Arrivo Pacco
                    </Button>
                  ) : (
                    <Button onClick={() => handleWorkflowAction('confirm_arrival')} disabled={isUpdating}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="voucher-confirm-arrival-btn">
                      {isUpdating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                      Conferma Arrivo e Genera Voucher
                    </Button>
                  )}
                  <Button onClick={() => setApprovalAction('reject_voucher')} disabled={isUpdating}
                    variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid="reject-voucher-btn">
                    <X className="w-4 h-4 mr-2" /> Rifiuta
                  </Button>
                </div>
              )}

              {/* Reject voucher textarea */}
              {approvalAction === 'reject_voucher' && (
                <div className="space-y-3 mt-4">
                  <Textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Motivo del rifiuto (verrà inviato al cliente)..." className="min-h-[80px] resize-none" data-testid="voucher-rejection-reason" />
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button onClick={() => { setApprovalAction(''); setRejectionReason(''); }} variant="outline" className="flex-1">Annulla</Button>
                    <Button onClick={() => handleWorkflowAction('reject')} disabled={isUpdating || !rejectionReason}
                      className="flex-1 bg-red-600 hover:bg-red-700" data-testid="confirm-reject-voucher-btn">
                      {isUpdating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <X className="w-4 h-4 mr-2" />}
                      Conferma Rifiuto
                    </Button>
                  </div>
                </div>
              )}

              {/* Pacco arrivato + final_approval → approva voucher */}
              {request?.arrival_confirmed_at && workflowConfig.voucher?.final_approval && !approvalAction && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button onClick={() => handleWorkflowAction('approve_voucher')} disabled={isUpdating}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="approve-voucher-btn">
                    {isUpdating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                    Approva Voucher
                  </Button>
                  <Button onClick={() => setApprovalAction('reject_voucher')} disabled={isUpdating}
                    variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid="reject-voucher-final-btn">
                    <X className="w-4 h-4 mr-2" /> Rifiuta
                  </Button>
                </div>
              )}
            </motion.div>
          )}

          {/* --- RMA: RICHIESTA → Approva Invio → IN_ATTESA_DI_ARRIVO (solo se initial_approval abilitato) --- */}
          {isRMA && (request?.status === 'RICHIESTA' || request?.status === 'requested') && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-amber-50 rounded-xl border border-amber-200 p-6"
            >
              <h3 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Reso Monetario — In Attesa di Approvazione
              </h3>
              <p className="text-sm text-amber-700 mb-4">Approva questa richiesta per permettere al cliente di spedire il pacco.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={() => handleWorkflowAction('approve_send')} disabled={isUpdating}
                  className="flex-1 bg-amber-600 hover:bg-amber-700" data-testid="approve-rma-send-btn">
                  {isUpdating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                  Approva Invio
                </Button>
              </div>
            </motion.div>
          )}

          {/* --- RMA: IN_ATTESA_DI_ARRIVO --- */}
          {isRMA && request?.status === 'IN_ATTESA_DI_ARRIVO' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-blue-50 rounded-xl border border-blue-200 p-6"
            >
              <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-500" />
                {request?.arrival_confirmed_at
                  ? 'Pacco Arrivato — Decisione Rimborso'
                  : 'Reso Monetario — Pacco in Arrivo'}
              </h3>
              <p className="text-sm text-blue-700 mb-4">
                {request?.arrival_confirmed_at
                  ? 'Il pacco è arrivato. Decidi se approvare o rifiutare il rimborso.'
                  : 'Il pacco è in arrivo. Conferma l\'arrivo per procedere.'}
              </p>
              
              {/* Pacco non ancora arrivato: conferma arrivo */}
              {!request?.arrival_confirmed_at && !approvalAction && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button onClick={() => handleWorkflowAction('confirm_arrival')} disabled={isUpdating}
                    className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="rma-confirm-arrival-btn">
                    {isUpdating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Package className="w-4 h-4 mr-2" />}
                    Conferma Arrivo Pacco
                  </Button>
                </div>
              )}

              {/* Pacco arrivato: mostra azioni di approvazione */}
              {request?.arrival_confirmed_at && !approvalAction && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button onClick={() => setApprovalAction('approve_refund')} disabled={isUpdating}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="approve-refund-btn">
                    <Check className="w-4 h-4 mr-2" /> Approva Rimborso
                  </Button>
                  <Button onClick={() => setApprovalAction('reject_refund')} disabled={isUpdating}
                    variant="outline" className="flex-1 border-red-300 text-red-600 hover:bg-red-50" data-testid="reject-refund-btn">
                    <X className="w-4 h-4 mr-2" /> Rifiuta Reso
                  </Button>
                </div>
              )}

              {approvalAction === 'reject_refund' && (
                <div className="space-y-3 mt-4">
                  <Textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Motivo del rifiuto..." className="min-h-[80px] resize-none" data-testid="rma-rejection-reason" />
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button onClick={() => { setApprovalAction(''); setRejectionReason(''); }} variant="outline" className="flex-1">Annulla</Button>
                    <Button onClick={() => handleWorkflowAction('reject_refund')} disabled={isUpdating || !rejectionReason}
                      className="flex-1 bg-red-600 hover:bg-red-700" data-testid="confirm-reject-refund-btn">
                      {isUpdating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <X className="w-4 h-4 mr-2" />}
                      Conferma Rifiuto
                    </Button>
                  </div>
                </div>
              )}

              {approvalAction === 'approve_refund' && (
                <div className="space-y-3 mt-4">
                  <p className="text-sm text-emerald-700">Confermi di voler approvare il rimborso? Verrà mostrato il link all'ordine Shopify per emettere il rimborso.</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button onClick={() => setApprovalAction('')} variant="outline" className="flex-1">Annulla</Button>
                    <Button onClick={() => handleWorkflowAction('approve_refund')} disabled={isUpdating}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-approve-refund-btn">
                      {isUpdating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                      Conferma Approvazione
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* --- RMA: Rimborso Approvato → Link Shopify + Conferma rimborso fatto --- */}
          {isRMA && request?.refund_approved_at && !request?.completed_at && request?.status !== 'COMPLETATO' && request?.status !== 'RESO_RIFIUTATO' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-emerald-50 rounded-xl border border-emerald-200 p-6"
            >
              <h3 className="font-semibold text-emerald-900 mb-3 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-emerald-500" />
                Rimborso Approvato — Emetti il Rimborso
              </h3>
              <p className="text-sm text-emerald-700 mb-4">Clicca il pulsante per aprire l'ordine su Shopify ed emettere il rimborso, poi torna qui per confermare.</p>
              
              {/* Auto-complete countdown */}
              {(() => {
                const approvedAt = new Date(request.refund_approved_at);
                const autoCompleteAt = new Date(approvedAt.getTime() + 15 * 60 * 1000);
                const now = new Date();
                const remaining = autoCompleteAt - now;
                if (remaining > 0) {
                  const hrs = Math.floor(remaining / 3600000);
                  const mins = Math.floor((remaining % 3600000) / 60000);
                  return (
                    <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-emerald-100 rounded-lg" data-testid="auto-complete-countdown">
                      <Clock className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      <p className="text-xs text-emerald-700">
                        Completamento automatico tra <strong>{hrs > 0 ? `${hrs}h ` : ''}{mins}min</strong> — oppure completa subito manualmente.
                      </p>
                    </div>
                  );
                }
                return (
                  <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-emerald-100 rounded-lg" data-testid="auto-complete-pending">
                    <Loader2 className="w-4 h-4 text-emerald-600 animate-spin flex-shrink-0" />
                    <p className="text-xs text-emerald-700">Completamento automatico in corso...</p>
                  </div>
                );
              })()}

              {request?.shopify_order_url && (
                <a href={request.shopify_order_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 w-full justify-center px-4 py-3 bg-white border-2 border-emerald-300 rounded-lg text-emerald-800 font-semibold hover:bg-emerald-50 transition-colors mb-4"
                  data-testid="shopify-order-link">
                  <ExternalLink className="w-5 h-5" />
                  Apri Ordine su Shopify
                </a>
              )}
              
              <Button onClick={() => handleWorkflowAction('confirm_refund_done')} disabled={isUpdating}
                className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-refund-done-btn">
                {isUpdating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Ho effettuato il rimborso — Completa
              </Button>
            </motion.div>
          )}

          {/* --- Legacy pending (for old requests) --- */}
          {!isRMA && (request?.status === 'pending' || request?.status === 'requested') && request?.request_type !== 'exchange' && request?.request_type !== 'voucher' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-amber-50 rounded-xl border border-amber-200 p-5"
            >
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800">In attesa di approvazione (legacy)</p>
                  <p className="text-sm text-amber-700 mt-1">Usa "Aggiorna Status" per assegnare il nuovo stato.</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* --- Legacy exchange pending → show payment info --- */}
          {!isRMA && request?.status === 'pending' && request?.request_type === 'exchange' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-amber-50 rounded-xl border border-amber-200 p-5"
            >
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800">Richiesta legacy — Cambio</p>
                  <p className="text-sm text-amber-700 mt-1">Usa "Aggiorna Status" per assegnare il nuovo stato corretto.</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* --- Legacy voucher pending → already handled as IN_ATTESA_DI_ARRIVO for new ones --- */}
          {!isRMA && request?.status === 'pending' && request?.request_type === 'voucher' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-amber-50 rounded-xl border border-amber-200 p-5"
            >
              <div className="flex items-start gap-3">
                <Gift className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800">Richiesta legacy — Voucher</p>
                  <p className="text-sm text-amber-700 mt-1">Usa "Aggiorna Status" per assegnare il nuovo stato corretto.</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Approve/Reject Out-of-Stock Exchange */}
          {request?.status === 'RICHIESTA_DA_VERIFICARE' && request?.has_unavailable_items && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-orange-50 rounded-xl border border-orange-200 p-6"
              data-testid="approve-exchange-section"
            >
              <h3 className="font-semibold text-orange-900 mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-orange-500" />
                Richiesta da Verificare — Prodotto Non Disponibile
              </h3>
              
              {request?.event_date && (
                <div className="mb-4 p-3 bg-white rounded-lg border border-orange-200">
                  <p className="text-xs text-gray-500 mb-1">Data Evento del Cliente</p>
                  <p className="font-semibold text-orange-800">
                    {format(parseISO(request.event_date), "d MMMM yyyy", { locale: it })}
                  </p>
                </div>
              )}

              {!approvalAction ? (
                <div className="flex gap-3">
                  <Button
                    onClick={() => setApprovalAction('approve')}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    data-testid="approve-exchange-btn"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Approva Cambio
                  </Button>
                  <Button
                    onClick={() => setApprovalAction('reject')}
                    variant="outline"
                    className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                    data-testid="reject-exchange-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Rifiuta Cambio
                  </Button>
                </div>
              ) : approvalAction === 'approve' ? (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-2 block">
                      Nota data prevista produzione (verrà inviata al cliente)
                    </label>
                    <Textarea
                      value={productionDateNote}
                      onChange={(e) => setProductionDateNote(e.target.value)}
                      placeholder="Es: L'abito sarà disponibile entro il 15 aprile 2026"
                      className="min-h-[80px] resize-none"
                      data-testid="production-date-note"
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button onClick={() => setApprovalAction('')} variant="outline" className="flex-1">Annulla</Button>
                    <Button
                      onClick={() => handleApproveRejectExchange('approve')}
                      disabled={isUpdating}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      data-testid="confirm-approve-btn"
                    >
                      {isUpdating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                      Conferma Approvazione
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-2 block">
                      Motivazione del rifiuto (verrà inviata al cliente)
                    </label>
                    <Textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Es: Non è possibile garantire la produzione entro la data del suo evento"
                      className="min-h-[80px] resize-none"
                      data-testid="rejection-reason-input-exchange"
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button onClick={() => setApprovalAction('')} variant="outline" className="flex-1">Annulla</Button>
                    <Button
                      onClick={() => handleApproveRejectExchange('reject')}
                      disabled={isUpdating || !rejectionReason}
                      className="flex-1 bg-red-600 hover:bg-red-700"
                      data-testid="confirm-reject-btn"
                    >
                      {isUpdating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <X className="w-4 h-4 mr-2" />}
                      Conferma Rifiuto
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Event Date display for exchanges (when already approved or any status) */}
          {request?.event_date && request?.status !== 'RICHIESTA_DA_VERIFICARE' && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-blue-50 rounded-xl border border-blue-200 p-5"
            >
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-xs text-gray-500">Data Evento Cliente</p>
                  <p className="font-semibold text-blue-800">{format(parseISO(request.event_date), "d MMMM yyyy", { locale: it })}</p>
                </div>
                {request?.production_date_note && (
                  <div className="ml-auto text-right">
                    <p className="text-xs text-gray-500">Nota produzione</p>
                    <p className="text-sm text-blue-700">{request.production_date_note}</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Completed Status */}
          {(request?.status === 'completed' || request?.status === 'COMPLETATO') && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-emerald-50 rounded-xl border border-emerald-200 p-5"
            >
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-800">Richiesta completata</p>
                  <p className="text-sm text-emerald-700 mt-1">
                    Questa richiesta è stata elaborata con successo.
                  </p>
                  {request?.completed_at && (
                    <p className="text-xs text-emerald-600 mt-2">
                      Completata il {toItalianTime(request.completed_at, 'full')}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Rejected Status */}
          {(request?.status === 'rejected' || request?.status === 'RESO_RIFIUTATO') && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-red-50 rounded-xl border border-red-200 p-5"
            >
              <div className="flex items-start gap-3">
                <X className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800">Reso rifiutato</p>
                  {request?.rejection_reason && (
                    <p className="text-sm text-red-700 mt-1">
                      Motivo: {request.rejection_reason}
                    </p>
                  )}
                  {request?.rejected_at && (
                    <p className="text-xs text-red-600 mt-2">
                      Rifiutato il {toItalianTime(request.rejected_at, 'full')}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Admin Notes */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl border border-gray-200 p-6"
          >
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-gray-400" />
              Note interne
            </h3>
            <Textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Aggiungi note..."
              className="min-h-[100px] resize-none mb-4"
            />
            <Button 
              onClick={() => handleStatusUpdate(request.status)}
              disabled={isUpdating}
              variant="outline"
              className="w-full"
            >
              Salva Note
            </Button>
          </motion.div>

          {/* Store Delivery Notification Section */}
          {request?.delivery_method === 'store' && request?.status !== 'RESO_RIFIUTATO' && request?.status !== 'ARCHIVIATO' && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.21 }}
              className="bg-white rounded-xl border border-teal-200 overflow-hidden"
              data-testid="store-delivery-section"
            >
              <div className="px-6 py-4 border-b border-teal-100 bg-teal-50">
                <h3 className="font-semibold text-teal-900 flex items-center gap-2">
                  <Store className="w-5 h-5 text-teal-500" />
                  Consegna in Negozio
                </h3>
              </div>
              <div className="p-6 space-y-4">
                {/* Ritirato in negozio (confermato dall'App Negozio) */}
                {request?.store_pickup_completed_at ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200" data-testid="store-pickup-completed-banner">
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-emerald-800">Pacco ritirato in negozio</p>
                        <p className="text-xs text-emerald-600">{formatDate(request.store_pickup_completed_at)}</p>
                        {request.store_pickup_completed_by && (
                          <p className="text-xs text-emerald-500 mt-0.5">Confermato da: {request.store_pickup_completed_by}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : request?.store_delivery_notified_at ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-emerald-800">Notifica inviata al cliente</p>
                        <p className="text-xs text-emerald-600">{formatDate(request.store_delivery_notified_at)}</p>
                      </div>
                    </div>
                    {(() => {
                      const notifiedAt = new Date(request.store_delivery_notified_at);
                      const notifDays = appSettings?.store_delivery_notification_days || 4;
                      const deadline = new Date(notifiedAt.getTime() + notifDays * 24 * 60 * 60 * 1000);
                      const now = new Date();
                      const daysLeft = Math.ceil((deadline - now) / (24 * 60 * 60 * 1000));
                      const isExpired = daysLeft <= 0;
                      return (
                        <div className={cn(
                          "p-3 rounded-lg border text-sm",
                          isExpired ? "bg-red-50 border-red-200" : daysLeft <= 1 ? "bg-amber-50 border-amber-200" : "bg-blue-50 border-blue-200"
                        )}>
                          <div className="flex items-center gap-2">
                            <Clock className={cn("w-4 h-4", isExpired ? "text-red-500" : daysLeft <= 1 ? "text-amber-500" : "text-blue-500")} />
                            <span className={cn("font-medium", isExpired ? "text-red-700" : daysLeft <= 1 ? "text-amber-700" : "text-blue-700")}>
                              {isExpired 
                                ? `Scaduto (termine superato da ${Math.abs(daysLeft)} giorn${Math.abs(daysLeft) === 1 ? 'o' : 'i'})` 
                                : `${daysLeft} giorn${daysLeft === 1 ? 'o' : 'i'} rimast${daysLeft === 1 ? 'o' : 'i'} per la consegna`
                              }
                            </span>
                          </div>
                          <p className="text-xs mt-1 opacity-75">
                            Scadenza: {format(deadline, 'd MMMM yyyy', { locale: it })}
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <p className="text-sm text-amber-700">Il cliente è in attesa della tua comunicazione per recarsi in negozio.</p>
                    </div>
                    <Button
                      onClick={handleNotifyStorePickup}
                      disabled={isNotifyingStore}
                      className="w-full bg-teal-600 hover:bg-teal-700 text-white"
                      data-testid="notify-store-pickup-btn"
                    >
                      {isNotifyingStore ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Invio in corso...</>
                      ) : (
                        <><Store className="w-4 h-4 mr-2" /> Invia Notifica "Vieni in Negozio"</>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Waybill Upload Section */}
          {waybillEnabled && request?.request_type !== 'voucher' && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.22 }}
              className="bg-white rounded-xl border border-indigo-200 overflow-hidden"
              data-testid="waybill-section"
            >
              <div className="px-6 py-4 border-b border-indigo-100 bg-indigo-50">
                <h3 className="font-semibold text-indigo-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-indigo-500" />
                  Lettera di Vettura
                </h3>
              </div>
              <div className="p-6">
                {request?.waybill ? (
                  <div className="space-y-4">
                    {/* Uploaded file info */}
                    <div className="flex items-center gap-3 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                      <FileText className="w-8 h-8 text-indigo-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-indigo-900 truncate" data-testid="waybill-filename">
                          {request.waybill.original_filename}
                        </p>
                        <p className="text-xs text-indigo-600">
                          Caricato il {formatDate(request.waybill.uploaded_at)}
                        </p>
                      </div>
                      <Button
                        onClick={handleDownloadWaybill}
                        variant="ghost"
                        size="sm"
                        className="text-indigo-600 hover:text-indigo-800 flex-shrink-0"
                        data-testid="download-waybill-btn"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Email status */}
                    {request.waybill.email_sent_at ? (
                      <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                        <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-emerald-800">Email inviata al cliente</p>
                          <p className="text-xs text-emerald-600">{formatDate(request.waybill.email_sent_at)}</p>
                        </div>
                      </div>
                    ) : (
                      <Button
                        onClick={() => setShowWaybillConfirm(true)}
                        className="w-full bg-indigo-600 hover:bg-indigo-700"
                        data-testid="send-waybill-email-btn"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Invia al Cliente via Email
                      </Button>
                    )}

                    {/* Replace waybill */}
                    <label className="block">
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handleWaybillUpload}
                        className="hidden"
                        data-testid="waybill-replace-input"
                      />
                      <span className="inline-flex items-center gap-2 text-xs text-gray-500 cursor-pointer hover:text-indigo-600 transition-colors">
                        <Upload className="w-3.5 h-3.5" />
                        Sostituisci file
                      </span>
                    </label>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500 mb-4">Nessuna lettera di vettura caricata</p>
                    <label className="block">
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handleWaybillUpload}
                        className="hidden"
                        data-testid="waybill-upload-input"
                      />
                      <span className={cn(
                        "inline-flex items-center gap-2 px-4 py-2.5 rounded-lg cursor-pointer transition-colors text-sm font-medium",
                        isUploadingWaybill
                          ? "bg-gray-100 text-gray-400"
                          : "bg-indigo-600 text-white hover:bg-indigo-700"
                      )}>
                        {isUploadingWaybill ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Caricamento...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Carica PDF
                          </>
                        )}
                      </span>
                    </label>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Manual Status Update */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-white rounded-xl border border-gray-200 p-6"
          >
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-gray-400" />
              Aggiorna Status
            </h3>
            
            {!showManualStatus ? (
              <Button 
                onClick={() => setShowManualStatus(true)}
                variant="outline"
                className="w-full"
                data-testid="show-manual-status-btn"
              >
                Cambia Stato Manualmente
              </Button>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-2 block">Nuovo Stato</label>
                  <select
                    value={manualNewStatus}
                    onChange={(e) => setManualNewStatus(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                    data-testid="manual-status-select"
                  >
                    <option value="">Seleziona stato...</option>
                    {MANUAL_STATUS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                
                {manualNewStatus === 'RESO_RIFIUTATO' && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-2 block">Motivazione Rifiuto</label>
                    <Textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Motivo del rifiuto..."
                      className="min-h-[80px] resize-none"
                      data-testid="rejection-reason-input"
                    />
                  </div>
                )}
                
                <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="suppressEmail"
                    checked={suppressEmail}
                    onChange={(e) => setSuppressEmail(e.target.checked)}
                    className="w-4 h-4 text-amber-600 rounded border-amber-300 focus:ring-amber-500"
                    data-testid="suppress-email-toggle"
                  />
                  <label htmlFor="suppressEmail" className="text-sm text-amber-800">
                    <span className="font-medium">Non inviare email</span>
                    <p className="text-xs text-amber-600 mt-0.5">
                      Seleziona per evitare di inviare email automatiche al cliente
                    </p>
                  </label>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={() => { setShowManualStatus(false); setManualNewStatus(''); }}
                    variant="outline"
                    className="flex-1"
                  >
                    Annulla
                  </Button>
                  <Button
                    onClick={handleManualStatusUpdate}
                    disabled={isUpdating || !manualNewStatus}
                    className="flex-1"
                    data-testid="confirm-manual-status-btn"
                  >
                    {isUpdating ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Conferma
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Waybill Send Confirmation Modal */}
      {showWaybillConfirm && request?.waybill && !request.waybill.email_sent_at && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl"
            data-testid="waybill-confirm-modal"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                <Mail className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Invia Lettera di Vettura</h3>
                <p className="text-sm text-gray-500">Conferma l'invio al cliente</p>
              </div>
            </div>
            
            <p className="text-gray-600 mb-2">
              Vuoi inviare la lettera di vettura <strong>{request.waybill.original_filename}</strong> a:
            </p>
            <p className="font-medium text-indigo-700 mb-4">{request.customer_email}</p>
            <p className="text-sm text-gray-500 mb-6">
              L'email conterra il PDF in allegato con le istruzioni per il ritiro.
            </p>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowWaybillConfirm(false)}
                disabled={isSendingWaybill}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={handleSendWaybillEmail}
                disabled={isSendingWaybill}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="confirm-send-waybill-btn"
              >
                {isSendingWaybill ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Invio...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Invia Email
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Elimina richiesta</h3>
                <p className="text-sm text-gray-500">Questa azione non può essere annullata</p>
              </div>
            </div>
            
            <p className="text-gray-600 mb-6">
              Eliminando questa richiesta, il cliente <strong>{request?.customer_email}</strong> potrà 
              effettuare una nuova richiesta per gli stessi articoli.
            </p>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Eliminazione...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Elimina
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminRequestDetail;
